import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useOutletContext, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAtom } from 'jotai';
import { Building2 } from 'lucide-react';
import {
    SitePerspectivePhase,
    SitePerspectiveProjectType,
    SitePerspectiveType,
} from '@the-real-insight/tri-model';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import ContentPanel from '../components/ContentPanel';
import { toast } from '@/components/ui/sonner';
import { useModalContext } from '../components/ModalProvider';
import { authenticationAtom } from '../State';
import type { AppShellContext } from '../components/AppShell';
import DocumentChimney from './DocumentChimney';
import SiteProposalDialog from './SiteProposalDialog';
import { analysedSince, proposalForBinding, statusesOf } from './siteProposalWatch';
import { missingSlots, requiredSlots } from './documentSlots';
import DataRoomView from './DataRoomView';

import { OpenDecision, useDocumentImport } from './documentImport';
import DecisionDialog from '../administration/document-analysis/DecisionDialog';
import MergeDialog, { MergeSubject } from './MergeDialog';
import ObjectOverview from './ObjectOverview';
import ObjectSelectionBar from './ObjectSelectionBar';
import { PhaseParcelResult, invalidatePhaseParcels, usePhaseParcels } from './phaseParcelsApi';
import PerspectiveDrillDown from './PerspectiveDrillDown';
import PhaseActivation from './PhaseActivation';
import PerspectiveTiles from './PerspectiveTiles';
import ProcessStepRail from './ProcessStepRail';
import { Perspective, ProcessStepKey, ProcessTypeKey, findStep, findType, stepIndex, stepTitle } from './processModel';
import { projectTypeOf } from './documentSlots';
import { pageBackground, stepColor, tintOf } from '../cbos/palette';
import { useBrandBase } from '../cbos/useBrandBase';
import { hasSampleContent, sampleSource } from './sampleContent';
import { PerspectiveScope, PerspectiveValues, getSitePerspectives, saveSitePerspective, changeListItem, saveInstanceValue } from './sitePerspectiveApi';
import { ProjectSite, buildingForSite, idAsString } from '../cbos/projectSites';
import { forgetProjects, useSelectedProject } from '../cbos/selectedProject';
import { SiteProposal, useDocumentAnalysisAPI } from '../DocumentAnalysisAPI';
import { footprintPolygon } from '../utilities/normalizeGeoShape';
import type { SlotActions } from './SlotEditors';

/**
 * Portfolio dashboard — one property seen through the process it runs through.
 *
 * Left the process rail (investment case → transaction → operation), middle the
 * object in view with its perspectives, right the document chimney. Step, object
 * and perspective all live in the URL, so a view can be linked to and survives a
 * reload.
 */

/** Identity of a perspective scope: the address plus what is being asked about it. */
const keyOfScope = (scope: PerspectiveScope): string =>
    [scope.street, scope.streetNumber, scope.postalCode, scope.city, scope.projectType, scope.phase]
        .map((part) => String(part ?? ''))
        .join('|');

export default function PortfolioDashboardPage() {
    const { setTitle } = useOutletContext<AppShellContext>();
    const { t, i18n } = useTranslation();
    const [searchParams, setSearchParams] = useSearchParams();

    const [site, setSite] = useState<ProjectSite | null>(null);
    const [building, setBuilding] = useState<any>(null);
    const [loadingBuilding, setLoadingBuilding] = useState(false);
    const [loadError, setLoadError] = useState<string | null>(null);
    /** The project's data room — resolved on first use, one per project and tenant. */
    const [dataRoomId, setDataRoomId] = useState<string | null>(null);
    /** Der Ordner des Projekts in der Ablage — die Struktur, über der die Analysen liegen. */
    const [dataRoomFolder, setDataRoomFolder] = useState<string | null>(null);
    /** Whether the data room is currently being resolved. */
    const [resolvingDataRoom, setResolvingDataRoom] = useState(false);
    /** Error from resolving the data room, if any. */
    const [dataRoomError, setDataRoomError] = useState<string | null>(null);
    // The project is chosen in the sidebar for the whole of CBOS; the page reads the choice rather
    // than offering one. Its name goes on the project's data room.
    const { project } = useSelectedProject();
    const projectName = (project?.name as string | undefined) ?? null;
    const [drillDown, setDrillDown] = useState<Perspective | null>(null);
    const [perspectiveValues, setPerspectiveValues] = useState<PerspectiveValues>({});
    /** Which scope the values in hand belong to — see `phaseActivated`. */
    const [loadedScopeKey, setLoadedScopeKey] = useState<string | null>(null);

    const documentAnalysisApi = useDocumentAnalysisAPI();
    const { pushError } = useModalContext() as any;
    const [authentication] = useAtom(authenticationAtom) as any;

    const type = findType(searchParams.get('type'));
    const step = findStep(type, searchParams.get('step'));
    const selectedProjectId = searchParams.get('project');
    const selectedSiteId = searchParams.get('site');
    const selectedPerspective = searchParams.get('perspective');

    const brand = useBrandBase();
    const color = stepColor(stepIndex(type, step), brand);
    const tint = useMemo(() => tintOf(color), [color]);

    const handleSiteChange = useCallback((next: ProjectSite | null) => setSite(next), []);

    // What the drill-down shows is re-resolved against the step in view instead
    // of trusting the perspective that was clicked. A step or type change would
    // otherwise leave the panel open on a perspective the step no longer has —
    // breadcrumb, title and content all naming something else than the rail.
    // Deriving it also covers the paths that never go through a click, such as
    // the browser's back button changing `?step=`.
    const openPerspective = useMemo(
        () => (drillDown ? (step.perspectives.find((entry) => entry.key === drillDown.key) ?? null) : null),
        [drillDown, step],
    );

    // An object is picked but no building stands behind it — a preset address
    // that resolves to nothing, or an id the server no longer knows. That is a
    // different situation from "nothing picked yet" and must not borrow its
    // wording, which would contradict the filled select box above.
    const buildingMissing = !!site && !loadingBuilding && !loadError && !building;
    // Drawn under the parcels so the plot can be read against the house that stands on it.
    const footprint = useMemo(() => footprintPolygon(building), [building]);

    /** Write one or more view parameters, dropping the ones set to null. */
    const setViewParams = useCallback(
        (changes: Record<string, string | null>) => {
            setSearchParams(
                (current) => {
                    const next = new URLSearchParams(current);
                    Object.entries(changes).forEach(([key, value]) => {
                        if (value === null) {
                            next.delete(key);
                        } else {
                            next.set(key, value);
                        }
                    });
                    return next;
                },
                { replace: true },
            );
        },
        [setSearchParams],
    );

    // A step change invalidates the picked perspective — the perspectives differ
    // per step, so carrying the key over would highlight nothing.
    const selectStep = useCallback(
        (key: ProcessStepKey) => setViewParams({ step: key, perspective: null }),
        [setViewParams],
    );

    // A type change can invalidate the step as well: an acquisition opens with
    // "Bewertung" where a new build opens with "Investment Case". Dropping both
    // lets findStep fall back to the new type's first step.
    const selectType = useCallback(
        (key: ProcessTypeKey) => setViewParams({ type: key, step: null, perspective: null }),
        [setViewParams],
    );

    /**
     * The building behind the picked site. A site is the portfolio's unit; the building is what
     * carries the footprint and the figures, and it is resolved the same way the project panel
     * resolves it.
     */
    useEffect(() => {
        if (!site) {
            setBuilding(null);
            setLoadError(null);
            return;
        }

        let cancelled = false;
        setLoadingBuilding(true);
        setLoadError(null);

        buildingForSite(site.site)
            .then((found) => {
                if (!cancelled) setBuilding(found);
            })
            .catch((error: any) => {
                console.error('Could not load the building of the site', error);
                if (!cancelled) {
                    setBuilding(null);
                    setLoadError(error?.message ?? t('global.generalError'));
                }
            })
            .finally(() => {
                if (!cancelled) setLoadingBuilding(false);
            });

        return () => {
            cancelled = true;
        };
    }, [site, t]);

    /**
     * The address a site perspective is written against, with the project type and phase this step
     * stands for. Null while the step is not backed by perspectives or nothing is picked — without
     * an address there is no key to upsert on.
     */
    const perspectiveScope: PerspectiveScope | null = useMemo(() => {
        // Hard-coded mapping for buildingRightCreation (old logic)
        if (step.key === 'buildingRightCreation') {
            const street = site?.street ?? building?.street ?? building?.address?.street;
            const city = site?.city ?? building?.city ?? building?.address?.city;
            if (!street && !city) return null;

            return {
                street,
                streetNumber: site?.streetNumber ?? building?.streetNumber ?? building?.address?.streetNumber,
                postalCode: site?.postalCode ?? building?.postalCode ?? building?.address?.postalCode,
                city,
                country: site?.country ?? building?.country ?? building?.address?.country,
                projectType: SitePerspectiveProjectType.build,
                phase: SitePerspectivePhase.analysis,
            };
        }

        // Model-backed phases: use projectTypeOf and the step key as the phase
        const projectType = projectTypeOf(type.key);
        if (!projectType) return null;

        const street = site?.street ?? building?.street ?? building?.address?.street;
        const city = site?.city ?? building?.city ?? building?.address?.city;
        if (!street && !city) return null;

        return {
            street,
            streetNumber: site?.streetNumber ?? building?.streetNumber ?? building?.address?.streetNumber,
            postalCode: site?.postalCode ?? building?.postalCode ?? building?.address?.postalCode,
            city,
            country: site?.country ?? building?.country ?? building?.address?.country,
            projectType,
            phase: step.key as SitePerspectivePhase,
        };
    }, [step.key, type.key, building, site]);

    useEffect(() => {
        if (!perspectiveScope) {
            setPerspectiveValues({});
            setLoadedScopeKey(null);
            return;
        }

        let cancelled = false;
        const scopeKey = keyOfScope(perspectiveScope);

        getSitePerspectives(perspectiveScope)
            .then((loaded) => {
                if (cancelled) return;
                setPerspectiveValues(loaded);
                setLoadedScopeKey(scopeKey);
            })
            .catch((error: any) => {
                console.error('Could not load the site perspectives', error);
                if (!cancelled) {
                    setPerspectiveValues({});
                    setLoadedScopeKey(scopeKey);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [perspectiveScope]);

    /**
     * A phase is activated exactly when its perspectives exist. No separate flag: a marker and the
     * documents could disagree, and then nobody would know which of the two to believe.
     *
     * The values only count for the scope they were loaded for. Clearing them on every scope change
     * made an activated phase flash its Activate button whenever the scope object was rebuilt;
     * keeping them made the *next* property inherit the previous one's unlocked tiles and figures.
     * Tying them to their scope answers both: same scope, no flash — different scope, not activated
     * until its own answer arrives.
     */
    const phaseKnown = !!perspectiveScope && loadedScopeKey === keyOfScope(perspectiveScope);
    const phaseActivated = phaseKnown && Object.keys(perspectiveValues).length > 0;
    /** Until the answer for this scope is in, the page says nothing about activation either way. */
    const phaseLoading = !!perspectiveScope && !phaseKnown;

    /**
     * The phase the loaded perspectives belong to. Every one of them carries the reference, so the
     * first is as good as any; it is absent for a phase activated before phases were rows, and then
     * there is nothing to delete by id.
     */
    const sitePhaseId: string | null = phaseActivated
        ? (idAsString(Object.values(perspectiveValues).find((perspective: any) => perspective?.sitePhase)?.sitePhase) ??
          null)
        : null;

    // The plot the site estimator worked out. One read for the page: the small map draws the
    // parcels and the cadastral drill-down lists them, and both should show the same thing.
    const parcelState = usePhaseParcels(phaseActivated ? perspectiveScope : null);

    const reloadPerspectives = useCallback(async () => {
        if (!perspectiveScope) return;
        // An activation derives the plot afresh, and a deletion takes it away. Either way what is
        // held about the parcels is from before.
        invalidatePhaseParcels();
        const scopeKey = keyOfScope(perspectiveScope);
        await getSitePerspectives(perspectiveScope)
            .then((loaded) => {
                setPerspectiveValues(loaded);
                setLoadedScopeKey(scopeKey);
            })
            .catch((error: any) => console.error('Could not reload the site perspectives', error));
    }, [perspectiveScope]);

    /**
     * A parcel decision, taken in one place and felt in three.
     *
     * The plot itself, the small map beside the property, and the figures the plot moved — the land
     * area on the cadastral tile and the two ratios on the zoning one. The server returns all of it
     * together; splitting the update would leave one of the three a request behind, which on a
     * screen showing all three at once is the difference nobody would explain.
     */
    const applyPlot = useCallback(
        (result: PhaseParcelResult) => {
            parcelState.apply(result);

            if (result.perspectives) {
                setPerspectiveValues((current) => ({ ...current, ...result.perspectives }));
            }
        },
        [parcelState],
    );

    /**
     * The writes of this page, one after the other.
     *
     * Every answer replaces the whole perspective on screen. A field, the organization chart and
     * the milestone plan can be written at nearly the same moment, and if two requests were in
     * flight at once, the older answer could arrive last and put back a state without the newer
     * change — which the next edit of the graph would then write back for good. In a row, each
     * answer includes everything written before it.
     */
    const writeQueue = useRef<Promise<unknown>>(Promise.resolve());
    /** The scope on screen now — a write that comes back for another one must not land on it. */
    const scopeOnScreen = useRef<string | null>(null);
    scopeOnScreen.current = perspectiveScope ? keyOfScope(perspectiveScope) : null;

    const savePerspective = useCallback(
        (
            perspectiveType: SitePerspectiveType,
            values: Record<string, any>,
            comments?: Record<string, string>,
        ): Promise<void> => {
            if (!perspectiveScope) return Promise.resolve();

            const writtenFor = keyOfScope(perspectiveScope);
            const run = writeQueue.current.then(async () => {
                const stored = await saveSitePerspective(perspectiveScope, perspectiveType, values, comments);
                // Written for a property or phase that is no longer on screen: the write stands, but
                // its answer describes the other one and would replace this one's perspective. The
                // load path drops stale answers the same way.
                if (scopeOnScreen.current !== writtenFor) return;
                // Take what came back rather than what was sent: the server coerces and clears, and
                // the tiles should show what is actually stored.
                setPerspectiveValues((current) => ({ ...current, [perspectiveType]: stored }));
            });
            // A failed write belongs to its caller; the next one in line runs regardless.
            writeQueue.current = run.catch(() => undefined);
            return run;
        },
        [perspectiveScope],
    );

    /**
     * Listenelemente und Werte gebundener Instanzen — über dieselbe Warteschlange wie das Formular,
     * damit zwei Schreibvorgänge an derselben Perspektive einander nicht überholen.
     */
    const slotActions = useMemo<SlotActions | undefined>(() => {
        if (!perspectiveScope) return undefined;
        const queued = (perspectiveType: SitePerspectiveType, write: () => Promise<Record<string, any>>): Promise<void> => {
            const writtenFor = keyOfScope(perspectiveScope);
            const run = writeQueue.current.then(async () => {
                const stored = await write();
                if (scopeOnScreen.current !== writtenFor) return;
                setPerspectiveValues((current) => ({ ...current, [perspectiveType]: stored }));
            });
            writeQueue.current = run.catch(() => undefined);
            return run;
        };
        return {
            changeListItem: (perspectiveType, name, change, comment) =>
                queued(perspectiveType, () => changeListItem(perspectiveScope, perspectiveType, name, change, comment)),
            saveInstanceValue: (perspectiveType, name, ref, value, comment) =>
                queued(perspectiveType, () => saveInstanceValue(perspectiveScope, perspectiveType, name, ref, value, comment)),
        };
    }, [perspectiveScope]);

    /**
     * The documents of a project belong to the project, not to one of its sites.
     *
     * A lease is read against the whole holding — the same document is evidence about several
     * addresses — so the room follows the project selection and survives a change of site.
     */
    useEffect(() => {
        if (!selectedProjectId) {
            setDataRoomId(null);
            setResolvingDataRoom(false);
            setDataRoomError(null);
            return;
        }

        let cancelled = false;
        setResolvingDataRoom(true);
        setDataRoomError(null);

        documentAnalysisApi
            .resolveProjectDataRoom(selectedProjectId, projectName ?? selectedProjectId)
            .then((result) => {
                if (!cancelled) {
                    setDataRoomId(result?.dataRoom?._id ?? null);
                    setDataRoomFolder(result?.folder?.path ?? null);
                    setResolvingDataRoom(false);
                }
            })
            .catch((error: any) => {
                console.error('Could not resolve the project data room', error);
                if (!cancelled) {
                    setDataRoomError(error?.message ?? t('global.generalError'));
                    setResolvingDataRoom(false);
                }
            });

        return () => {
            cancelled = true;
        };
        // The API object is rebuilt on every render; its behaviour is stable.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedProjectId, projectName]);

    const documentImport = useDocumentImport(dataRoomId, (error) =>
        console.error('Document import failed', error),
    );

    /**
     * Was die gewählte Phase verlangt und im Datenraum noch fehlt.
     *
     * Der Raum zeigt sonst nur, was da ist — und die Frage vor einer Prüfung ist die umgekehrte.
     * Verglichen wird über Rolle und Untertyp: Wie eine Datei heißt, ist Zufall; was sie *ist*, hat
     * die Auswertung festgestellt, und genau darauf zeigt die Anforderung.
     */
    const missingDocuments = useMemo(
        () => missingSlots(requiredSlots(type.key, step.key), documentImport.analyses as any[]),
        [type.key, step.key, documentImport.analyses],
    );

    /** The question currently being asked, if any — see the chimney's "n offene Entscheidungen". */
    const [openDecision, setOpenDecision] = useState<OpenDecision | null>(null);
    const [openMerge, setOpenMerge] = useState<MergeSubject | null>(null);

    /**
     * Die Frage nach einer neuen Liegenschaft, wenn eine Auswertung fertig geworden ist.
     *
     * Der Gedanke: Ein Datenraum soll sein Projekt aufbauen können. Nennt ein ausgewertetes Dokument
     * ein Gebäude, dessen Anschrift unter den Liegenschaften fehlt, wird gefragt — nicht ergänzt.
     * Ein Dokument nennt auch Nachbaradressen und Vergleichsobjekte.
     */
    const [siteQuestion, setSiteQuestion] = useState<{
        analysisId: string;
        documentName: string;
        proposals: SiteProposal[];
    } | null>(null);

    /**
     * Dieselbe Frage nach einer eben getroffenen Zuordnung.
     *
     * Der zweite Weg, auf dem eine Bindung entsteht: Wo der Abgleich sich nicht entscheiden konnte,
     * wählt später ein Mensch. Kommt dabei ein Gebäude heraus, dessen Anschrift das Projekt nicht
     * kennt, ist die Frage dieselbe wie beim Abschluss einer Auswertung — sie wird nur zu einem
     * anderen Zeitpunkt gestellt.
     *
     * Eingegrenzt auf das *gewählte* Gebäude: Sonst käme mit jeder beantworteten Zuordnung wieder
     * hoch, was beim Abschluss schon einmal abgelehnt wurde.
     */
    const askForSiteAfterBinding = useCallback(
        async (decision: OpenDecision, candidate: any) => {
            if (!candidate || String(candidate.className ?? '') !== 'Building') return;

            try {
                const { proposals } = await documentAnalysisApi.getSiteProposals(decision.analysisId);
                const match = proposalForBinding(proposals, candidate);

                if (!match) return;

                // Erst die Entscheidung schließen: Zwei Dialoge übereinander sind einer zu viel.
                setOpenDecision(null);
                setSiteQuestion({
                    analysisId: decision.analysisId,
                    documentName: decision.documentName,
                    proposals: [match],
                });
            } catch (error) {
                // Eine Frage, die nicht gestellt werden kann, ist kein Grund für eine Meldung: Die
                // Zuordnung ist getroffen, und das war der Auftrag.
                console.error('Could not read the site proposals after a binding', error);
            }
        },
        // Das API-Objekt wird bei jedem Zeichnen neu gebaut; sein Verhalten ist stabil.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [],
    );

    /**
     * Der Stand der Läufe beim letzten Blick.
     *
     * In einer Referenz, weil er zwischen zwei Blicken verglichen und nicht gezeichnet wird. Der
     * erste Blick ist die Aussaat: Was da schon fertig ist, gilt als bekannt — sonst legten sich
     * beim Öffnen des Dashboards so viele Dialoge übereinander, wie das Projekt Dokumente hat.
     */
    const seenAnalyses = useRef<Map<string, string> | null>(null);

    useEffect(() => {
        const rows = documentImport.analyses as any[];
        const before = seenAnalyses.current;

        // Erster Blick: nur säen, nicht fragen.
        if (!before || siteQuestion) {
            seenAnalyses.current = statusesOf(rows);
            return;
        }

        const finished = analysedSince(before, rows);
        if (finished.length === 0) {
            seenAnalyses.current = statusesOf(rows);
            return;
        }

        let cancelled = false;

        // Der erste, zu dem es etwas zu fragen gibt. Mehrere gleichzeitig wären mehrere Dialoge
        // übereinander; die übrigen kommen beim nächsten Lauf oder beim nächsten Mal wieder hoch.
        void (async () => {
            for (const analysisId of finished) {
                try {
                    const { proposals } = await documentAnalysisApi.getSiteProposals(analysisId);
                    if (cancelled || proposals.length === 0) continue;

                    const row = rows.find((entry) => String(entry?._id) === analysisId);

                    setSiteQuestion({
                        analysisId,
                        documentName: String(row?.document?.fileName ?? ''),
                        proposals,
                    });
                    seenAnalyses.current = statusesOf(rows);
                    return;
                } catch (error) {
                    // Eine Frage, die nicht gestellt werden kann, ist kein Grund für eine Meldung
                    // über die Seite — das Dokument ist ausgewertet, und das war der Auftrag.
                    console.error('Could not read the site proposals', error);
                }
            }

            seenAnalyses.current = statusesOf(rows);
        })();

        return () => {
            cancelled = true;
        };
        // Das API-Objekt wird bei jedem Zeichnen neu gebaut; sein Verhalten ist stabil.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [documentImport.analyses]);

    useEffect(() => {
        // Named where the reader already looks. The project qualifies every figure on the page, and
        // it is no longer picked on the page itself — the title is what carries it now.
        setTitle(projectName ? `${t('portfolioDashboard.title')} · ${projectName}` : t('portfolioDashboard.title'));
    }, [setTitle, t, projectName]);

    return (
        <div className="flex min-h-0 flex-1 flex-col">
            <ContentPanel paddingClassName="p-3" backgroundColor={pageBackground(brand)}>
                <div className="grid w-full grid-cols-1 items-start gap-3 lg:grid-cols-[180px_minmax(0,1fr)] xl:grid-cols-[208px_minmax(0,1fr)_28%]">
                    <ProcessStepRail
                        type={type}
                        selectedStep={step.key}
                        onSelectType={selectType}
                        onSelectStep={selectStep}
                    />

                    <div className="flex min-w-0 flex-col gap-3">
                        <Card>
                            <div className="flex min-h-[52px] flex-wrap items-center justify-between gap-2 border-b px-3 py-2">
                                <ObjectSelectionBar
                                    selectedProjectId={selectedProjectId}
                                    selectedSiteId={selectedSiteId}
                                    onSelect={(projectId, siteId) =>
                                        setViewParams({ project: projectId, site: siteId })
                                    }
                                    onSiteChange={handleSiteChange}
                                />
                                <PhaseActivation
                                    scope={phaseLoading ? null : perspectiveScope}
                                    activated={phaseActivated}
                                    sitePhaseId={sitePhaseId}
                                    phaseLabel={t(`portfolioDashboard.phases.${SitePerspectivePhase.analysis}`)}
                                    onActivated={reloadPerspectives}
                                    onDeleted={reloadPerspectives}
                                />
                            </div>
                            <div className="p-3.5">
                                {loadingBuilding ? (
                                    // Skeleton rather than a spinner: what is being waited for is a
                                    // map beside a column of figures, and its shape says so.
                                    <div
                                        role="status"
                                        aria-label={t('global.loading')}
                                        className="grid items-stretch gap-3.5 md:grid-cols-[260px_minmax(0,1fr)]"
                                    >
                                        <Skeleton className="h-[170px] w-full rounded-lg" />
                                        <div className="grid content-start gap-x-6 sm:grid-cols-2">
                                            {Array.from({ length: 8 }).map((_entry, index) => (
                                                <Skeleton key={index} className="my-1 h-4 w-full" />
                                            ))}
                                        </div>
                                    </div>
                                ) : loadError ? (
                                    <Alert variant="destructive">
                                        <AlertDescription>{loadError}</AlertDescription>
                                    </Alert>
                                ) : building ? (
                                    <ObjectOverview
                                        building={building}
                                        fallbackLabel={site?.label}
                                        parcels={parcelState.rows}
                                    />
                                ) : (
                                    <div className="flex min-h-[130px] flex-col items-center justify-center gap-2 text-center text-muted-foreground">
                                        <Building2 size={30} strokeWidth={1.4} />
                                        <div className="text-sm">
                                            {buildingMissing
                                                ? t('portfolioDashboard.objectSelection.buildingMissing')
                                                : t('portfolioDashboard.objectSelection.noObject')}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </Card>

                        <Card>
                            <div className="flex min-h-[52px] flex-wrap items-center justify-between gap-2 border-b px-3 py-2">
                                <span className="text-xs font-bold uppercase tracking-[0.09em] text-muted-foreground">
                                    {stepTitle(step, t, i18n.language ?? 'de')}
                                </span>
                                {hasSampleContent(type.key, step.key) ? (
                                    <Badge
                                        variant="warning"
                                        className="uppercase tracking-wide"
                                        title={t('portfolioDashboard.sample.source', {
                                            object: sampleSource.object,
                                            document: sampleSource.document,
                                        })}
                                    >
                                        {t('portfolioDashboard.sample.badge')}
                                    </Badge>
                                ) : (
                                    <></>
                                )}
                            </div>
                            <div className="p-3.5">
                                <PerspectiveTiles
                                    type={type}
                                    step={step}
                                    color={color}
                                    selectedPerspective={selectedPerspective}
                                    onSelectPerspective={(key) => setViewParams({ perspective: key })}
                                    perspectiveScope={perspectiveScope}
                                    onExpandPerspective={setDrillDown}
                                    perspectiveValues={phaseActivated ? perspectiveValues : undefined}
                                    perspectivesLocked={phaseKnown && !phaseActivated}
                                    perspectivesLoading={phaseLoading}
                                />
                            </div>
                        </Card>
                    </div>

                    {/* Narrow windows: the chimney moves below the other two columns rather than
                        being squeezed to a width where its file names no longer fit. */}
                    <div className="lg:col-span-2 xl:sticky xl:top-0 xl:col-span-1">
                        <DocumentChimney
                            color={color}
                            tint={tint}
                            disabled={!dataRoomId || resolvingDataRoom || !!dataRoomError}
                            disabledHint={
                                dataRoomError ??
                                t(
                                    resolvingDataRoom
                                        ? 'portfolioDashboard.documents.preparing'
                                        : 'portfolioDashboard.documents.needsProject',
                                )
                            }
                        >
                            {/* Der Baum ist der Körper des Kamins: eine Liste statt zweier, seit die
                                Analyse ihre Datei in den Projektordner ablegt. */}
                            <DataRoomView
                                folderPath={dataRoomFolder}
                                embedded
                                onChanged={() => documentImport.reload()}
                                onRemoveAnalysis={(analysisId) => documentImport.remove(analysisId)}
                                onAnalyse={(files, folderPath) => documentImport.upload(files, folderPath)}
                                pending={documentImport.pending}
                                progress={documentImport.progress}
                                decisions={documentImport.decisions}
                                onDecide={setOpenDecision}
                                awaitingMerge={documentImport.awaitingMerge}
                                onMerge={(analysis) =>
                                    setOpenMerge({
                                        analysisId: analysis._id,
                                        documentName: analysis.document?.fileName ?? '',
                                        summary: analysis.mergeSummary,
                                    })
                                }
                                missing={missingDocuments}
                            />
                        </DocumentChimney>
                    </div>
                </div>
            </ContentPanel>
            {/* The question, in the shape it is best asked: a table of candidates does not fit a
                20% column, and a decision is an interruption that is answered once. */}
            <DecisionDialog
                subject={
                    openDecision
                        ? {
                              extractedObjectId: openDecision.extractedObjectId,
                              term: openDecision.term,
                              className: openDecision.className,
                              candidates: openDecision.candidates as any,
                              searchedKey: openDecision.searchedKey,
                              searchedValues: openDecision.searchedValues,
                              documentName: openDecision.documentName,
                          }
                        : null
                }
                onClose={() => setOpenDecision(null)}
                onDecide={async (_subject, candidate) => {
                    if (!openDecision) return;

                    await documentImport.decide(openDecision, candidate);
                    await askForSiteAfterBinding(openDecision, candidate);
                }}
            />

            {/* Die letzte Stelle vor dem Bestand. Bis hierher hat kein Schritt etwas geschrieben. */}
            <MergeDialog
                subject={openMerge}
                onClose={() => setOpenMerge(null)}
                onMerge={(analysisId) => documentImport.merge(analysisId)}
            />

            {/* Die Frage nach einer neuen Liegenschaft — siehe den Effekt weiter oben. */}
            {siteQuestion ? (
                <SiteProposalDialog
                    analysisId={siteQuestion.analysisId}
                    documentName={siteQuestion.documentName}
                    proposals={siteQuestion.proposals}
                    /* Diese Anschrift nicht — die nächste schon: Jede ist eine eigene
                       Entscheidung, und eine Absage zu einer ist keine zu allen.
                       Die Absage wird gemerkt: Sonst fragt jedes weitere Dokument über dasselbe
                       Haus erneut, und ein Datenraum enthält gerade das. */
                    onSkip={() => {
                        const declined = siteQuestion.proposals[0];

                        if (declined) {
                            void documentAnalysisApi
                                .declineSiteProposal(siteQuestion.analysisId, declined.buildingId)
                                .catch((error: any) =>
                                    // Eine nicht gemerkte Absage ist ein Ärgernis, kein Fehler: Die
                                    // Frage kommt wieder, angelegt wurde nichts.
                                    console.error('Could not remember the declined address', error),
                                );
                        }

                        const remaining = siteQuestion.proposals.slice(1);
                        setSiteQuestion(remaining.length > 0 ? { ...siteQuestion, proposals: remaining } : null);
                    }}
                    /* Weggeklickt heißt: gar nicht jetzt. */
                    onDismiss={() => setSiteQuestion(null)}
                    onAccept={async (proposal) => {
                        try {
                            await documentAnalysisApi.acceptSiteProposal(
                                siteQuestion.analysisId,
                                proposal.buildingId,
                            );

                            /**
                             * Die Projektliste vergessen, sonst zeigt die Liegenschaftsauswahl
                             * weiter den Stand von vorhin: Sie liest die Liegenschaften aus dem
                             * zwischengespeicherten Projekt, und genau das hat sich gerade geändert.
                             */
                            forgetProjects(authentication?.customer);
                            toast.success(
                                t('portfolioDashboard.siteProposal.added', { address: proposal.label }),
                            );

                            // Die nächste Anschrift derselben Auswertung, sofern es eine gibt. Jede ist
                            // eine eigene Entscheidung.
                            const remaining = siteQuestion.proposals.slice(1);
                            setSiteQuestion(remaining.length > 0 ? { ...siteQuestion, proposals: remaining } : null);
                        } catch (error) {
                            pushError(error);
                        }
                    }}
                />
            ) : (
                <></>
            )}

            <PerspectiveDrillDown
                type={type}
                step={step}
                perspective={openPerspective}
                objectLabel={site?.label}
                onDismiss={() => setDrillDown(null)}
                perspectiveValues={phaseActivated ? perspectiveValues : {}}
                onSavePerspective={savePerspective}
                slotActions={slotActions}
                canEdit={!!perspectiveScope && phaseActivated}
                perspectivesLocked={phaseKnown && !phaseActivated}
                perspectiveScope={perspectiveScope}
                parcelState={parcelState}
                footprint={footprint}
                onParcelsChanged={applyPlot}
            />
        </div>
    );
}
